import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import {
  ArrowLeft,
  User,
  Building2,
  Hash,
  FileText,
  Briefcase,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useAuthStore } from '@/features/auth/stores/auth.store';

interface ClientData {
  id: number;
  type: string;
  name?: string;
  lastname?: string;
  middlename?: string;
  createdAt?: string;
}

interface ClientDetailHeaderProps {
  data: ClientData;
  displayName: string;
  isPerson: boolean;
  contractCount: number;
}

export function ClientDetailHeader({
  data,
  displayName,
  isPerson,
  contractCount,
}: ClientDetailHeaderProps) {
  const { t } = useTranslation();
  const hasAccess = useAuthStore((state) => state.hasAccess);

  return (
    <>
      {/* Hero Header Section */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <Link
            to="/app/clients"
            className="inline-flex items-center text-sm font-medium text-slate-600 hover:text-slate-900 mb-6 transition-colors"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('backToClients')}
          </Link>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div className="flex items-center gap-5">
              <div
                className={`h-16 w-16 rounded-full flex items-center justify-center border-4 border-white shadow-lg ${
                  isPerson ? 'bg-blue-100' : 'bg-purple-100'
                }`}
              >
                {isPerson ? (
                  <User className="h-8 w-8 text-blue-600" />
                ) : (
                  <Building2 className="h-8 w-8 text-purple-600" />
                )}
              </div>

              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
                  {displayName}
                </h1>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 border border-slate-300 text-xs font-medium uppercase tracking-wide text-slate-700">
                    <Hash className="h-3.5 w-3.5" />
                    {t('client')} #{String(data.id).padStart(5, '0')}
                  </span>
                  <Badge
                    variant="outline"
                    className={`text-xs font-medium ${isPerson ? 'border-blue-300 text-blue-700 bg-blue-50' : 'border-purple-300 text-purple-700 bg-purple-50'}`}
                  >
                    {isPerson ? t('person') : t('companyu')}
                  </Badge>
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              {hasAccess('client', 'update') && (
                <Link to={`/app/clients/action${data.type}/${data.id}`}>
                  <Button variant="outline" size="sm">
                    {t('actions.edit')}
                  </Button>
                </Link>
              )}
              {hasAccess('contract', 'create') && (
                <Link to={`/app/contracts/action?clientId=${data.id}`}>
                  <Button size="sm">{t('newContract')}</Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="bg-slate-50 border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex flex-wrap items-center justify-center sm:justify-start gap-6 sm:gap-8">
            <div className="text-center sm:text-left">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">
                {t('type')}
              </p>
              <p className="text-xl font-bold text-slate-900">
                {isPerson ? t('person') : t('companyu')}
              </p>
            </div>
            <Separator orientation="vertical" className="h-10 hidden sm:block" />
            <div className="text-center sm:text-left">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">
                {t('contracts')}
              </p>
              <p className="text-xl font-bold text-blue-600">{contractCount}</p>
            </div>
            <Separator orientation="vertical" className="h-10 hidden sm:block" />
            <div className="text-center sm:text-left">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">
                {t('memberSince')}
              </p>
              <p className="text-xl font-bold text-slate-900">
                {data.createdAt ? format(new Date(data.createdAt), 'MMM yyyy') : '-'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions Card (at header level for full-width layout) */}
      <div className="max-w-6xl mx-auto px-6 pt-6">
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4">
          <p className="text-lg font-semibold text-slate-900 mb-3">{t('quickActions')}</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {hasAccess('contract', 'create') && (
              <Link
                to={`/app/contracts/action?clientId=${data.id}`}
                className="flex items-center gap-3 p-3 rounded-md border border-slate-200 hover:border-blue-300 hover:bg-blue-50/50 transition-colors"
              >
                <FileText className="h-5 w-5 text-slate-400" />
                <span className="text-sm font-medium">{t('newContract')}</span>
              </Link>
            )}
            <Link
              to={`/app/matters?clientId=${data.id}`}
              className="flex items-center gap-3 p-3 rounded-md border border-slate-200 hover:border-blue-300 hover:bg-blue-50/50 transition-colors"
            >
              <Briefcase className="h-5 w-5 text-slate-400" />
              <span className="text-sm font-medium">{t('viewMatters')}</span>
            </Link>
            {hasAccess('client', 'update') && (
              <Link
                to={`/app/clients/action${data.type}/${data.id}`}
                className="flex items-center gap-3 p-3 rounded-md border border-slate-200 hover:border-blue-300 hover:bg-blue-50/50 transition-colors"
              >
                <User className="h-5 w-5 text-slate-400" />
                <span className="text-sm font-medium">{t('editClient')}</span>
              </Link>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
